import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, CheckCircle } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, PageLoading } from '../components/ui/Section';
import { useSiteContent } from '../hooks/useSiteContent';
import EmbeddedDonationForm from '../components/ui/EmbeddedDonationForm';

gsap.registerPlugin(ScrollTrigger);

const PageContainer = styled.div`
  background: ${props => props.theme.colors.background};
`;

const HeroSection = styled.div<{ imageUrl: string }>`
  position: relative;
  width: 100%;
  height: 500px;
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(16, 18, 16, 0.35) 0%, rgba(16, 18, 16, 0.85) 100%);
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    height: 400px;
  }
`;

const HeroContent = styled(Container)`
  position: relative;
  z-index: 2;
  padding-top: ${props => props.theme.spacing[10]};
  padding-bottom: ${props => props.theme.spacing[12]};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  color: white;
  text-decoration: none;
  margin-bottom: ${props => props.theme.spacing[8]};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  color: rgba(255, 255, 255, 0.8);
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
  width: fit-content;
  margin-bottom: ${props => props.theme.spacing[5]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${props => props.theme.borderRadius.full};
    background: ${props => props.theme.colors.primary[400]};
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  color: ${props => props.theme.colors.white};
  max-width: 18ch;
  margin-bottom: ${props => props.theme.spacing[5]};
`;

const HeroDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  color: rgba(255, 255, 255, 0.78);
  max-width: 56ch;
`;

const ContentSection = styled.section`
  padding: ${props => props.theme.sectionPadding.lg} 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing[12]};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1.7fr 1fr;
    gap: ${props => props.theme.spacing[16]};
  }
`;

const MainContent = styled.div`
  h2 {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    color: ${props => props.theme.colors.ink};
    margin-bottom: ${props => props.theme.spacing[5]};
  }

  p {
    font-size: ${props => props.theme.typography.fontSize.lg};
    color: ${props => props.theme.colors.textMuted};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
    margin-bottom: ${props => props.theme.spacing[10]};
    max-width: 62ch;
  }
`;

const Sidebar = styled.div``;

const PartnerCard = styled.div`
  padding: ${props => props.theme.spacing[6]};
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: ${props => props.theme.spacing[4]};

  h3 {
    font-family: ${props => props.theme.typography.fontFamily.body};
    font-size: ${props => props.theme.typography.fontSize.xs};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    text-transform: uppercase;
    letter-spacing: ${props => props.theme.typography.letterSpacing.eyebrow};
    color: ${props => props.theme.colors.textMuted};
    margin-bottom: ${props => props.theme.spacing[3]};
  }

  p {
    font-family: ${props => props.theme.typography.fontFamily.heading};
    font-size: ${props => props.theme.typography.fontSize.xl};
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    letter-spacing: ${props => props.theme.typography.letterSpacing.tight};
    color: ${props => props.theme.colors.ink};
    margin-bottom: ${props => props.theme.spacing[4]};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const PartnerLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  color: ${props => props.theme.colors.primary[700]};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.primary[500]};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const HighlightsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${props => props.theme.spacing[8]} 0;
`;

const HighlightItem = styled.li`
  display: flex;
  align-items: start;
  gap: ${props => props.theme.spacing[4]};
  padding: ${props => props.theme.spacing[5]} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: ${props => props.theme.colors.primary[500]};
    flex-shrink: 0;
    margin-top: 3px;
    width: 18px;
    height: 18px;
  }

  span {
    font-size: ${props => props.theme.typography.fontSize.base};
    color: ${props => props.theme.colors.neutral[700]};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
  }
`;

const DonationSection = styled.section`
  padding: ${props => props.theme.sectionPadding.lg} 0;
  background: ${props => props.theme.colors.surface};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const DonationHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing[12]};

  h2 {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    color: ${props => props.theme.colors.ink};
    margin: 0 auto ${props => props.theme.spacing[4]};
    max-width: 16ch;
  }

  p {
    font-size: ${props => props.theme.typography.fontSize.lg};
    color: ${props => props.theme.colors.textMuted};
    max-width: 54ch;
    margin: 0 auto;
  }
`;

const InitiativeDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { siteContent, isLoading: isLoadingSiteContent } = useSiteContent();

  const mainContentRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const highlightsRef = useRef<HTMLUListElement>(null);
  const donationSectionRef = useRef<HTMLDivElement>(null);

  // Find initiative directly without live data hook (not needed on detail page)
  const initiative = slug && siteContent?.initiatives
    ? [
        ...siteContent.initiatives.current,
        ...siteContent.initiatives.past,
        ...siteContent.initiatives.upcoming
      ].find(init => init.slug === slug)
    : null;

  useEffect(() => {
    if (isLoadingSiteContent || !initiative) return;

    // Animate main content from left
    if (mainContentRef.current) {
      gsap.fromTo(mainContentRef.current,
        { opacity: 0, x: -40 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: mainContentRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    // Animate sidebar from right
    if (sidebarRef.current) {
      gsap.fromTo(sidebarRef.current,
        { opacity: 0, x: 40 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sidebarRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    // Animate highlights with stagger
    if (highlightsRef.current) {
      const items = highlightsRef.current.children;
      gsap.fromTo(items,
        { opacity: 0, x: -20 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: highlightsRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    // Animate donation section
    if (donationSectionRef.current) {
      gsap.fromTo(donationSectionRef.current,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: donationSectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isLoadingSiteContent, initiative]);

  if (isLoadingSiteContent || !initiative) {
    return <PageLoading />;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
    });
  };

  return (
    <PageContainer>
      <HeroSection imageUrl={initiative.imageUrl}>
        <HeroContent>
          <BackButton to="/initiatives">
            <ArrowLeft size={20} />
            Back to Initiatives
          </BackButton>
          <Badge>{initiative.status === 'current' ? 'Active Campaign' : initiative.status}</Badge>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {initiative.title}
          </HeroTitle>
          <HeroDescription>{initiative.shortDescription}</HeroDescription>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          {/* Main Content + Sidebar */}
          <ContentGrid>
            <MainContent ref={mainContentRef}>
              <h2>About This Initiative</h2>
              <p>{initiative.longDescription}</p>

              {initiative.highlights.length > 0 && (
                <>
                  <h2>Key Highlights</h2>
                  <HighlightsList ref={highlightsRef}>
                    {initiative.highlights.map((highlight, index) => (
                      <HighlightItem key={index}>
                        <CheckCircle size={20} />
                        <span>{highlight}</span>
                      </HighlightItem>
                    ))}
                  </HighlightsList>
                </>
              )}
            </MainContent>

            <Sidebar ref={sidebarRef}>
              <PartnerCard>
                <h3>Partner Organization</h3>
                <p>{initiative.partnerOrganization}</p>
                {initiative.partnerUrl && (
                  <PartnerLink href={initiative.partnerUrl} target="_blank" rel="noopener noreferrer">
                    Learn more <ExternalLink />
                  </PartnerLink>
                )}
              </PartnerCard>

              <PartnerCard>
                <h3>Initiative Started</h3>
                <p>{formatDate(initiative.startDate)}</p>
              </PartnerCard>
            </Sidebar>
          </ContentGrid>
        </Container>
      </ContentSection>

      {/* Donation Form Section */}
      <DonationSection ref={donationSectionRef}>
        <Container>
          <DonationHeader>
            <h2>Support This Initiative</h2>
            <p>Every donation makes a direct impact. Join us in creating meaningful change.</p>
          </DonationHeader>
          <EmbeddedDonationForm
            config={{
              url: initiative.zeffyUrl,
              title: 'Donation form powered by Zeffy',
              height: '1100px',
              allowPaymentRequest: true,
              allowTransparency: true
            }}
          />
        </Container>
      </DonationSection>
    </PageContainer>
  );
};

export default InitiativeDetail;
